"use client";

import { useLoginModal } from "@/context/LoginModalContext";
import { usePassportProvider } from "@/context/PassportProvider";
import { notifyError } from "@/utils/monitoring";
import { authorizedDelete, authorizedPost } from "@/utils/request";
import { usePrevious } from "@biom3/react";
import { useCallback, useEffect, useState } from "react";
import { mutate as globalMutate } from "swr";
import { useGetWishlistedGames } from "./api";
import { useAccessToken } from "./useAccessToken";

const API_PATH = "/v1/games/wishlist";

type PendingWishlistAction = {
  gameId: string;
  action: "add" | "remove";
};

/**
 * Wishlist hook for grid/list contexts with multiple games
 * Returns functions that take gameId as parameters
 * Uses GlobalWishlistProvider for wishlist state
 */
export const useWishlistGrid = () => {
  const { passportState, walletAddress } = usePassportProvider();
  const previousPassportState = usePrevious(passportState);
  const getAccessToken = useAccessToken();
  const { openLoginModal } = useLoginModal();
  const [loadingGames, setLoadingGames] = useState<Set<string>>(new Set());
  const [pendingAction, setPendingAction] = useState<
    PendingWishlistAction | undefined
  >(undefined);

  // Fetch wishlist using SWR - automatically deduplicates across components
  const { wishlistedGames, isLoading: isFetchingWishlistedGames } =
    useGetWishlistedGames({
      walletAddress: walletAddress || null,
      isPassportWallet: passportState.authenticated && passportState.ready,
    });

  // Add a game to wishlist
  const addToWishlist = useCallback(
    async (gameId: string) => {
      // Prevent action while fetching wishlist
      if (isFetchingWishlistedGames) {
        return;
      }

      if (!passportState.authenticated) {
        setPendingAction({ gameId, action: "add" });
        openLoginModal();
        return;
      }

      if (passportState.authenticated && !passportState.zkEvmRegistered) {
        // Wallet creation is in progress, save pending action
        setPendingAction({ gameId, action: "add" });
        return;
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        notifyError("Unauthorized post", "wishlist");
        return;
      }

      setLoadingGames((prev) => new Set(prev).add(gameId));

      try {
        await authorizedPost(API_PATH, { game_id: gameId }, accessToken);

        // Optimistically update the global wishlist cache
        if (walletAddress) {
          globalMutate(
            `v1/games/wishlist/${walletAddress}`,
            (currentGames: string[] | undefined) => {
              const games = currentGames || [];
              return games.includes(gameId) ? games : [...games, gameId];
            },
            { revalidate: true },
          );
        }
      } catch (e) {
        notifyError("Failed to add game to wishlist", "wishlist");
      } finally {
        setLoadingGames((prev) => {
          const next = new Set(prev);
          next.delete(gameId);
          return next;
        });
      }
    },
    [
      isFetchingWishlistedGames,
      passportState,
      openLoginModal,
      getAccessToken,
      walletAddress,
    ],
  );

  // Remove a game from wishlist
  const removeFromWishlist = useCallback(
    async (gameId: string) => {
      // Prevent action while fetching wishlist
      if (isFetchingWishlistedGames) {
        return;
      }

      if (!passportState.authenticated) {
        setPendingAction({ gameId, action: "remove" });
        openLoginModal();
        return;
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        notifyError("Unauthorized delete", "wishlist");
        return;
      }

      setLoadingGames((prev) => new Set(prev).add(gameId));

      try {
        await authorizedDelete(API_PATH, accessToken, { game_id: gameId });

        // Optimistically update the cache
        if (walletAddress) {
          globalMutate(
            `v1/games/wishlist/${walletAddress}`,
            (currentGames: string[] | undefined) => {
              const games = currentGames || [];
              return games.filter((id) => id !== gameId);
            },
            { revalidate: true },
          );
        }
      } catch (e) {
        notifyError("Failed to remove game from wishlist", "wishlist");
      } finally {
        setLoadingGames((prev) => {
          const next = new Set(prev);
          next.delete(gameId);
          return next;
        });
      }
    },
    [
      isFetchingWishlistedGames,
      passportState,
      openLoginModal,
      getAccessToken,
      walletAddress,
    ],
  );

  // Toggle wishlist status for a game
  const toggleWishlist = useCallback(
    async (gameId: string) => {
      const isWishlisted = wishlistedGames?.includes(gameId);
      if (isWishlisted) {
        await removeFromWishlist(gameId);
      } else {
        await addToWishlist(gameId);
      }
    },
    [wishlistedGames, removeFromWishlist, addToWishlist],
  );

  // Execute pending action after user authenticates and wallet is registered
  useEffect(() => {
    const wasNotAuthenticated = previousPassportState?.authenticated === false;
    const isNowAuthenticated = passportState.authenticated;
    const wasNotRegistered = !previousPassportState?.zkEvmRegistered;
    const isNowRegistered = passportState.zkEvmRegistered;

    // Execute pending action when user completes authentication and registration
    if (
      pendingAction &&
      ((isNowAuthenticated && wasNotAuthenticated) ||
        (isNowRegistered && wasNotRegistered))
    ) {
      // Only execute if fully authenticated and registered
      if (passportState.authenticated && passportState.zkEvmRegistered) {
        if (pendingAction.action === "add") {
          addToWishlist(pendingAction.gameId);
        } else {
          removeFromWishlist(pendingAction.gameId);
        }
        setPendingAction(undefined);
      }
    }
  }, [
    passportState,
    previousPassportState,
    pendingAction,
    addToWishlist,
    removeFromWishlist,
  ]);

  // Helper functions that take gameId parameters
  const isGameWishlisted = useCallback(
    (gameId: string): boolean => {
      return wishlistedGames?.includes(gameId) ?? false;
    },
    [wishlistedGames],
  );

  const isGameLoading = useCallback(
    (gameId: string): boolean => {
      return loadingGames.has(gameId) || isFetchingWishlistedGames;
    },
    [loadingGames, isFetchingWishlistedGames],
  );

  return {
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isGameWishlisted,
    isGameLoading,
    isFetchingWishlistedGames,
  };
};
